/* External */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { List } from 'immutable';

/* Storage */
import { selectAllSchedules } from '../storage/schedules';

/* Utils */
import { PROVIDERS } from '../utils/constants';

/* Resolves the channel's schedule ids to records straight from the entity
   collection, memoized on the store's stable references (the channel's
   schedules List, its provider, and the collection itself) — NOT on the
   channel object, which is rebuilt whenever the channels array churns, and
   NOT through selectScheduleListByIdsList, whose single shared memo entry
   thrashes across rows and returns a fresh List identity per call. An
   unstable result identity re-fires every downstream effect on every render
   (the NewEpg anchor effect loops setFocus/setState — "Maximum update depth
   exceeded" while sitting on a loading card).

   The id in channel.schedules IS the schedule entity's normalized id
   (idAttribute: `<channelId>_<start>-<provider>` — the provider suffix is
   already part of it, e.g. `-Vrio` or `-Spideo` when the feed supplies
   `source`). Resolve it RAW first — appending `-<provider>` again (the
   previous lookup) missed every suffixed id and silently dropped the
   program from the row (the BAND row losing its current program, the
   anchor snapping to a past one: no "now" marker, the timeline jumping). */
export default function useChannelSchedules(channel) {
  const schedules = channel?.schedules;
  const sourceProvider = channel?.sourceProvider;
  const entities = useSelector(selectAllSchedules);
  return useMemo(
    () => (schedules ?? List())
      .map((schedule) => {
        const scheduleId = String(schedule);
        return entities.get(scheduleId)
          ?? entities.get(`${scheduleId}-${sourceProvider ?? PROVIDERS.VRIO}`);
      })
      .filter((schedule) => !!schedule)
      .toList(),
    [schedules, sourceProvider, entities],
  );
}
